import styled from '@emotion/styled';
import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { Fragment, useCallback } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { SkillProficiencyLevel } from '~/shared/data-contract/skill';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import type { TranslationKey } from '~/shared/internationalization/translation';
import { ScrollArea } from '~/ui/core/scroll-area/ScrollArea';
import { Tile, TileBase, TileContent } from '~/ui/core/tile/Tile';
import { TileFooter } from '~/ui/core/tile/TileFooter';
import { SidepanelHeader } from '~/ui/domain/Sidepanel/components/SidepanelHeader';
import { ProficiencyScale } from '~/ui/domain/skill/ProficiencyScale';

import { useSkillProficiencyLevelName } from '../../app/common/skills';
import { useSkillDetails } from './api/use-skill-details';
import { useSkillLocales } from './api/use-skill-locales';
import { SkillDetailsLoadingSkeleton } from './SkillDetailsLoadingSkeleton';
import type { NavigatableSidepanelItem } from './types';
import { useItemNavigation } from './use-item-navigation';
import { useSetupLanguageFromLocales } from './use-setup-language-from-locales';

const SkillDetailTile = styled(TileBase)({
    position: 'relative',
    zIndex: 1,
});

const StickyHeader = styled.div`
    position: sticky;
    top: 0;
    padding-top: ${({ theme }) => theme.spacing.xxl};
    z-index: 1;
    background-color: ${({ theme }) => theme.colors.white[200]};
    padding-bottom: ${({ theme }) => theme.spacing.xxl};
    margin: 0;
    padding-left: 0;
    padding-right: 0;
    width: 100%;
`;

const ProficiencyLevelTerm = styled.dt`
    display: flex;
    justify-content: space-between;
    padding-bottom: ${({ theme }) => theme.spacing.xxl};
    padding-top: ${({ theme }) => theme.spacing.xxl};
`;

const Title = styled.div(({ theme }) => ({
    color: theme.colors.black[900],
    flexShrink: 0,
    fontSize: theme.typography.headline.h4.medium.fontSize,
    fontWeight: theme.typography.headline.h4.medium.fontWeight,
    lineHeight: 'normal',
    minWidth: '100%',
    textAlign: 'left',
    width: 'min-content',
}));

const Dl = styled.dl(({ theme }) => ({
    '& > dd': {
        color: theme.colors.gray[800],
        margin: 0,
        marginBottom: theme.spacing['3xl'],
    },

    '& > dt': {
        ...theme.typography.body.S.semibold,
        paddingBottom: 0,
    },
}));

export const SkillsDetailsPanel: FunctionComponent<{
    readonly id: string;
    readonly availableItems?: readonly NavigatableSidepanelItem[];
    readonly disableArrows?: boolean;
    readonly onClose: () => void;
    readonly onNavigate: (skillId: string) => void;
    readonly onPreferredLanguageChange?: (language: LanguageCodeForJSA) => void;
    readonly preferredLanguage?: LanguageCodeForJSA;
    readonly proficiency?: SkillProficiencyLevel | null;
}> = ({
    id,
    availableItems,
    disableArrows = false,
    onClose,
    onNavigate,
    onPreferredLanguageChange,
    preferredLanguage,
    proficiency,
}) => {
    const translate = useTranslate();

    const { data: locales } = useSkillLocales();

    const { activeLanguage, availableLanguages, setActiveLanguage } = useSetupLanguageFromLocales(
        locales,
        preferredLanguage,
    );

    const { data, isError, isLoading } = useSkillDetails(id, activeLanguage);

    const { goToNextItem, goToPreviousItem } = useItemNavigation(availableItems, id, onNavigate);

    const { getProficiencyLevelName } = useSkillProficiencyLevelName();

    const handleActiveLanguageChange = useCallback(
        (language: LanguageCodeForJSA): void => {
            setActiveLanguage(language);
            onPreferredLanguageChange?.(language);
        },
        [onPreferredLanguageChange, setActiveLanguage],
    );

    const header = (
        <StickyHeader>
            <SidepanelHeader
                activeLanguage={
                    is.undefined(activeLanguage)
                        ? undefined
                        : translate(`common.languages.${activeLanguage}` as TranslationKey)
                }
                disableArrows={disableArrows}
                isGenerating={false}
                isLanguagePickerDisabled={false}
                languages={availableLanguages}
                setActiveLanguage={handleActiveLanguageChange}
                onClose={onClose}
                onNext={goToNextItem}
                onPrevious={goToPreviousItem}
            />
        </StickyHeader>
    );

    if (isLoading) {
        return (
            <Tile data-testid="skill-details-loading">
                <ScrollArea>
                    <TileContent>
                        {header}
                        <SkillDetailsLoadingSkeleton />
                    </TileContent>
                </ScrollArea>
            </Tile>
        );
    }

    if (isError) {
        return (
            <Tile>
                {header}
                <div>{translate('pages.job-architecture-v2.skill-details-error')}</div>
            </Tile>
        );
    }

    if (is.undefined(data)) {
        return (
            <Tile>
                {header}
                <div>{translate('pages.job-architecture-v2.skill-details-no-data')}</div>
            </Tile>
        );
    }

    return (
        <SkillDetailTile
            data-testid="skill-details"
            className="[&_[data-radix-scroll-area-viewport]>div]:block! [&_[data-radix-scroll-area-viewport]>div]:h-full"
        >
            <ScrollArea>
                <TileContent className="flex min-h-full w-full items-start">
                    {header}
                    <div className="flex-1">
                        <Title>{data.title}</Title>
                        <p>{data.description}</p>
                        <Dl>
                            {data.proficiencyLevelDetails?.map((detail, index) => (
                                // biome-ignore lint/suspicious/noArrayIndexKey: suppressed due to migration
                                <Fragment key={index}>
                                    <ProficiencyLevelTerm>
                                        {getProficiencyLevelName({
                                            level: detail.proficiencyLevel,
                                            skillType: data.skillType,
                                        })}
                                        <ProficiencyScale
                                            active={proficiency === detail.proficiencyLevel}
                                            level={detail.proficiencyLevel}
                                            scaleCount={5}
                                        />
                                    </ProficiencyLevelTerm>
                                    <dd>{detail.description}</dd>
                                </Fragment>
                            ))}
                        </Dl>
                    </div>
                    <TileFooter />
                </TileContent>
            </ScrollArea>
        </SkillDetailTile>
    );
};
